/**
 * 微信环境诊断工具
 * 用于排查微信配置和录音问题
 */

import { isWechatReady, getWechatConfigUrl } from '@/utils/wechat.js'
import { platformDetector } from '@/utils/platform.js'

/**
 * 运行完整的微信环境诊断
 */
export function runWechatDiagnosis() {
  console.info('🔍 ========== 微信环境诊断开始 ==========')
  
  // 1. 检查微信环境
  const isWeChat = /MicroMessenger/i.test(navigator.userAgent)
  console.info('1️⃣ 微信环境检测:', isWeChat ? '✅ 是' : '❌ 否')
  
  if (!isWeChat) {
    console.warn('⚠️ 当前不在微信环境中')
    return
  }
  
  // 设备平台检测
  const isIOS = platformDetector.isIOS()
  const isAndroid = platformDetector.isAndroid()
  console.info('📱 设备平台:', isIOS ? 'iOS' : isAndroid ? 'Android' : '其他')
  
  // 2. 检查微信SDK加载
  const wxLoaded = !!window.wx
  console.info('2️⃣ 微信SDK加载:', wxLoaded ? '✅ 已加载' : '❌ 未加载')
  
  if (!wxLoaded) {
    console.error('❌ 微信SDK未加载，请检查 index.html 中的 jweixin 引用')
    return
  }
  
  // 3. 检查SDK初始化状态
  const sdkReady = isWechatReady()
  console.info('3️⃣ 微信SDK初始化:', sdkReady ? '✅ 已就绪' : '❌ 未就绪')
  
  // 4. 检查配置URL
  const configUrl = getWechatConfigUrl()
  console.info('4️⃣ 配置URL:', configUrl || '未配置')
  
  // 5. URL对比
  const currentUrl = window.location.href
  const currentCleanUrl = window.location.href.split('#')[0]
  console.info('5️⃣ 当前URL:', currentUrl)
  console.info('   当前URL（无hash）:', currentCleanUrl)
  
  if (configUrl) {
    try {
      const configPath = new URL(configUrl).pathname
      const currentPath = new URL(currentCleanUrl).pathname
      
      if (configPath !== currentPath) {
        console.warn('⚠️ 配置URL路径与当前URL路径不一致')
        console.warn('  配置URL路径:', configPath)
        console.warn('  当前URL路径:', currentPath)
        console.info('💡 SPA应用中路径不一致是正常的（路由切换导致）')
        console.info('💡 已自动缓存入口URL，签名使用的是固定URL')
      } else {
        console.info('✅ URL路径一致')
      }
    } catch (error) {
      console.error('❌ URL解析失败:', error)
    }
  }
  
  // 6. 检查录音API是否可用
  if (wxLoaded) {
    const apis = ['startRecord', 'stopRecord', 'uploadVoice', 'playVoice']
    console.info('6️⃣ 录音API检测:')
    apis.forEach(api => {
      const available = typeof window.wx[api] === 'function'
      console.info(`  ${api}:`, available ? '✅' : '❌')
    })
  }
  
  // 7. 检查权限状态
  console.info('7️⃣ 浏览器信息:')
  console.info('  UserAgent:', navigator.userAgent)
  console.info('  语言:', navigator.language)
  console.info('  平台:', navigator.platform)
  
  // 8. 检查网络状态
  console.info('8️⃣ 网络状态:')
  console.info('  在线:', navigator.onLine ? '✅' : '❌')
  
  console.info('🔍 ========== 微信环境诊断结束 ==========')
}

/**
 * 测试微信录音功能
 */
export async function testWechatRecord() {
  console.info('🎤 ========== 微信录音测试开始 ==========')
  
  if (!window.wx) {
    console.error('❌ 微信SDK未加载')
    return
  }
  
  if (!isWechatReady()) {
    console.error('❌ 微信SDK未初始化')
    return
  }
  
  try {
    console.info('🎤 尝试开始录音...')
    
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('录音启动超时'))
      }, 5000)
      
      window.wx.startRecord({
        success: () => {
          clearTimeout(timeout)
          console.info('✅ 录音开始成功')
          
          // 立即停止
          setTimeout(() => {
            window.wx.stopRecord({
              success: (res) => {
                console.info('✅ 录音停止成功, localId:', res.localId)
                resolve()
              },
              fail: (err) => {
                console.error('❌ 录音停止失败:', err.errMsg)
                reject(err)
              }
            })
          }, 1000)
        },
        fail: (res) => {
          clearTimeout(timeout)
          console.error('❌ 录音开始失败:', res.errMsg)
          console.error('❌ 完整错误:', JSON.stringify(res))
          reject(res)
        }
      })
    })
    
    console.info('✅ 录音测试通过')
  } catch (error) {
    console.error('❌ 录音测试失败:', error.message || error.errMsg)
  }
  
  console.info('🎤 ========== 微信录音测试结束 ==========')
}

/**
 * 在全局暴露诊断函数，方便在控制台调用
 */
if (typeof window !== 'undefined') {
  window.__wechatDiagnosis = runWechatDiagnosis
  window.__testWechatRecord = testWechatRecord
  
  console.info('💡 提示: 可在控制台调用以下函数进行诊断:')
  console.info('  - window.__wechatDiagnosis() // 运行完整诊断')
  console.info('  - window.__testWechatRecord() // 测试录音功能')
}


